import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FiCheckCircle, FiAlertTriangle, FiAlertCircle, FiX } from 'react-icons/fi';
import '../styles/Toast.css';

export type ToastType = 'success' | 'warning' | 'error';

interface ToastItem {
  id: number;
  type: ToastType;
  message: string;
}

interface ToastContextValue {
  /** Generic entry point — prefer showSuccess/showWarning/showError below. */
  showToast: (type: ToastType, input: unknown, durationMs?: number) => void;
  showSuccess: (input: unknown, durationMs?: number) => void;
  showWarning: (input: unknown, durationMs?: number) => void;
  showError: (input: unknown, durationMs?: number) => void;
}

const ToastContext = createContext<ToastContextValue>({
  showToast: () => {},
  showSuccess: () => {},
  showWarning: () => {},
  showError: () => {},
});

const DEFAULT_DURATION_MS = 4500;

// Callers pass all sorts of things here — a plain string, a caught Error, or a
// raw API error payload (some endpoints pass through Anthropic's own error
// shape: {type, error: {type, message}, request_id}). Whatever it is, pull out
// ONE human-readable line — never the type/id/request_id fields, and never a
// raw "[object Object]" from stringifying something that isn't a string.
function toToastMessage(input: unknown): string {
  if (typeof input === 'string') return input.trim() || 'Something went wrong.';
  if (input instanceof Error) return input.message || 'Something went wrong.';
  if (input && typeof input === 'object') {
    const obj = input as Record<string, unknown>;
    if (typeof obj.message === 'string' && obj.message.trim()) return obj.message;
    // One level of nesting — e.g. the raw {error: {message}} passthrough shape.
    if (obj.error && typeof obj.error === 'object') {
      const nested = (obj.error as Record<string, unknown>).message;
      if (typeof nested === 'string' && nested.trim()) return nested;
    }
    if (typeof obj.errorMessage === 'string' && obj.errorMessage.trim()) return obj.errorMessage;
  }
  return 'Something went wrong.';
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const idRef = useRef(0);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const showToast = useCallback((type: ToastType, input: unknown, durationMs = DEFAULT_DURATION_MS) => {
    const id = ++idRef.current;
    const message = toToastMessage(input);
    setToasts((prev) => [...prev, { id, type, message }]);
    const timer = setTimeout(() => dismiss(id), durationMs);
    timersRef.current.set(id, timer);
  }, [dismiss]);

  const showSuccess = useCallback((input: unknown, durationMs?: number) => showToast('success', input, durationMs), [showToast]);
  const showWarning = useCallback((input: unknown, durationMs?: number) => showToast('warning', input, durationMs), [showToast]);
  const showError = useCallback((input: unknown, durationMs?: number) => showToast('error', input, durationMs), [showToast]);

  const ICONS: Record<ToastType, React.ReactNode> = {
    success: <FiCheckCircle size={16} />,
    warning: <FiAlertTriangle size={16} />,
    error: <FiAlertCircle size={16} />,
  };

  return (
    <ToastContext.Provider value={{ showToast, showSuccess, showWarning, showError }}>
      {children}
      {ReactDOM.createPortal(
        <div className="amp-toast-stack">
          {toasts.map((t) => (
            <div key={t.id} className={`amp-toast amp-toast--${t.type}`} role="status">
              <span className="amp-toast__icon">{ICONS[t.type]}</span>
              <span className="amp-toast__message">{t.message}</span>
              <button
                type="button"
                className="amp-toast__close"
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss notification"
              >
                <FiX size={14} />
              </button>
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);
